import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import Quagga from '@ericblade/quagga2';
import { Router } from '@angular/router';
import { faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons';
import { SharedDataService } from '../services/shared-data.service';
import { LocalStorageService } from '../../local-storage.service';
import { getMainBarcodeScanningCamera } from './camera-access';
import { ToastrService } from 'ngx-toastr';
import { MessageBoxService } from 'src/app/services/message-box.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-scan',
  templateUrl: './scan.component.html',
  styleUrls: ['./scan.component.scss'],
})
export class ScanComponent implements OnInit, OnDestroy, AfterViewInit {
  title = 'BarcodeExample';
  started: boolean | undefined;
  errorMessage: string | undefined;

  faArrowCircleLeft = faArrowCircleLeft;
  public currentLang!: string;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private sharedService: SharedDataService,
    private changeDetectorRef: ChangeDetectorRef,
    private localStorage: LocalStorageService,
    private messageBoxService: MessageBoxService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.localStorage.get('language', 'SwissTimeEntry', true).then((res: any) => {
      this.currentLang = res;
    });
  }

  ngAfterViewInit(): void {
    if (
      !navigator.mediaDevices ||
      !(typeof navigator.mediaDevices.getUserMedia === 'function')
    ) {
      this.errorMessage = 'getUserMedia is not supported';
      return;
    }

    this.initializeScanner();
  }

  ngOnDestroy(): void {
    Quagga.stop();
  }

  private initializeScanner(): Promise<void> {
    if (
      !navigator.mediaDevices ||
      !(typeof navigator.mediaDevices.getUserMedia === 'function')
    ) {
      this.errorMessage =
        'getUserMedia is not supported. Please use Chrome on Android or Safari on iOS';
      this.started = false;
      return Promise.reject(this.errorMessage);
    }

    // enumerate devices and do some heuristics to find a suitable first camera
    return Quagga.CameraAccess.enumerateVideoDevices()
      .then((mediaDeviceInfos) => {
        const mainCamera = getMainBarcodeScanningCamera(mediaDeviceInfos);
        if (mainCamera) {
          console.log(
            `Using ${mainCamera.label} (${mainCamera.deviceId}) as initial camera`
          );
          return this.initializeScannerWithDevice(mainCamera.deviceId);
        } else {
          console.error(
            `Unable to determine suitable camera, will fall back to default handling`
          );
          return this.initializeScannerWithDevice(undefined);
        }
      })
      .catch((error) => {
        this.errorMessage = `Failed to enumerate devices: ${error}`;
        this.started = false;
      });
  }

  private initializeScannerWithDevice(
    preferredDeviceId: string | undefined
  ): Promise<void> {
    console.log(`Initializing Quagga scanner...`);

    const constraints: MediaTrackConstraints = {};
    if (preferredDeviceId) {
      // if we have a specific device, we select that
      constraints.deviceId = preferredDeviceId;
    } else {
      // otherwise we tell the browser we want a camera facing backwards (note that browser does not always care about this)
      constraints.facingMode = 'environment';
    }

    return Quagga.init(
      {
        inputStream: {
          type: 'LiveStream',
          constraints,
          target: document.querySelector('#scanner-container') ?? undefined,
        },
        decoder: {
          readers: ['code_39_reader', 'code_39_vin_reader'],
          multiple: false,
          debug: {
            drawBoundingBox: true,
            showFrequency: true,
            drawScanline: true,
            showPattern: true,
          },
        },
        // See: https://github.com/ericblade/quagga2/blob/master/README.md#locate
        locate: false,
        debug: true,
      },
      (err) => {
        if (err) {
          console.error(`Quagga initialization failed: ${err}`);
          this.errorMessage = `Initialization error: ${err}`;
          this.started = false;
        } else {
          console.log(`Quagga initialization succeeded`);
          Quagga.start();
          this.started = true;

          Quagga.onDetected((res) => {
            console.log(`On Detect`, res);
            try {
              this.onBarcodeScanned(res.codeResult.code);
            } catch (ex) {
              this.translate.get("MAIN_APP_DIALOG.ERROR_DIALOG.BARCODE_ERROR.NO_CODE.text").subscribe((t:string) => {
                this.messageBoxService.showError(t, "");
              });
            }
          });
        }
      }
    );
  }

  onBarcodeScanned(code: string | null) {
    if (!code) throw "no code in result";
    this.changeDetectorRef.detectChanges();
    this.sharedService.scannedData(code);
    Quagga.stop();
    this.router.navigate(['/timeapp']);
  }

  goBack() {
    this.router.navigateByUrl('/timeapp');
  }
}
